import React, { useEffect, useState } from "react";
import PartnerLayout, { tenantSlugFor } from "@/components/PartnerLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CalendarDays } from "lucide-react";

const STATUS_STYLES = {
  planning: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  completed: "bg-brand-orange-500/10 text-brand-orange-300 border-primary/30",
  cancelled: "bg-red-500/10 text-red-300 border-red-500/30",
};

export default function PartnerCohortes() {
  const { user } = useAuth();
  const slug = tenantSlugFor(user);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/partner/${slug}/cohorts`)
      .then((r) => setCohorts(r.data))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <PartnerLayout user={user}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Cohortes</p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">Cohortes activas y planeadas</h1>
        <p className="mt-2 text-gray-400">Vista read-only. Para modificaciones contacta a tu asesor DCC.</p>

        {loading ? (
          <div className="mt-8 h-40 bg-[#111827] border border-gray-800 rounded-xl animate-pulse" />
        ) : cohorts.length === 0 ? (
          <div className="mt-8 p-8 border border-dashed border-gray-800 rounded-xl text-center text-gray-400">
            <CalendarDays className="w-10 h-10 text-gray-600 mx-auto mb-3" aria-hidden="true" />
            <p>Sin cohortes registradas todavía.</p>
          </div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="partner-cohorts-grid">
            {cohorts.map((c) => (
              <article key={c.id} className="p-5 bg-[#111827] border border-gray-800 rounded-xl">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-gray-500">{c.program_code}</p>
                    <p className="mt-1 font-semibold text-white">{c.name}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold border ${STATUS_STYLES[c.status] || STATUS_STYLES.planning}`}>{c.status}</span>
                </div>
                <p className="mt-3 text-xs text-gray-400">{c.program_title}</p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-[10px] uppercase text-gray-500">Inicio</p>
                    <p className="mt-0.5 font-mono text-white">{c.start_date?.slice(0, 10)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-gray-500">Fin</p>
                    <p className="mt-0.5 font-mono text-white">{c.end_date?.slice(0, 10)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-gray-500">Cupo</p>
                    <p className="mt-0.5 font-mono text-white">{c.enrolled_count || 0}/{c.capacity || 0}</p>
                  </div>
                </div>
                {(c.sessions || []).length > 0 && (
                  <div className="mt-4 pt-3 border-t border-gray-800">
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Próximas sesiones</p>
                    <ul className="space-y-1">
                      {c.sessions.slice(0, 3).map((s) => (
                        <li key={s.id} className="text-xs text-gray-300 font-mono">
                          {new Date(s.scheduled_at).toLocaleString("es-MX")} — {s.title || "Sesión"}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </PartnerLayout>
  );
}
